/**
 * Interface for parsed coverage format
 */
export interface CoverageFormat {
  percentage: number | null;
  value: string | null;
}

// Currency formatter for consistent formatting
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Convert a string dollar amount to a number
 * @param value Dollar amount string (e.g. "$1,234" or "1,234")
 * @returns number or null if invalid
 */
function parseDollarAmount(value: string): number | null {
  const numericString = value.replace(/[$,]/g, '');
  const parsed = parseFloat(numericString);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Parse a coverage string into its dollar amount and percentage components.
 * Handles various formats:
 * - "$10,000 (20%)"
 * - "$10,000 (20.5%)"
 * - "(20%) $10,000"
 * - "(20.5%) $10,000"
 * - "20% $10,000"
 * - "20.5% $10,000"
 * - "$10,000" (dollar amount only)
 * - "20.5%" (percentage only)
 *
 * @param value The value to parse
 * @returns Object containing the parsed percentage and dollar amount
 */
export function parseCoverageValue(value: unknown): CoverageFormat {
  const defaultResult: CoverageFormat = {
    percentage: null,
    value: null,
  };

  if (!value || typeof value !== 'string') {
    return defaultResult;
  }

  const input = value.toString();

  // Try matching format with parentheses first: "(XX.XX%) $Y,YYY"
  const withParentheses = input.match(/\((\d+\.?\d*)%\)\s*(\$[\d,]+)|\$[\d,]+\s*\((\d+\.?\d*)%\)/);
  if (withParentheses) {
    const percentage = withParentheses[1] || withParentheses[3];
    const dollarMatch = input.match(/\$([\d,]+)/);
    const amount = dollarMatch ? parseDollarAmount(dollarMatch[0]) : null;
    return {
      percentage: parseFloat(percentage),
      value: amount !== null ? currencyFormatter.format(amount) : null,
    };
  }

  // Try matching format without parentheses: "XX.XX% $Y,YYY"
  const withoutParentheses = input.match(/(\d+\.?\d*)%\s*(\$[\d,]+)|\$[\d,]+\s*(\d+\.?\d*)%/);
  if (withoutParentheses) {
    const percentage = withoutParentheses[1] || withoutParentheses[3];
    const dollarMatch = input.match(/\$([\d,]+)/);
    const amount = dollarMatch ? parseDollarAmount(dollarMatch[0]) : null;
    return {
      percentage: parseFloat(percentage),
      value: amount !== null ? currencyFormatter.format(amount) : null,
    };
  }

  // Check for dollar amount only
  const dollarAmountOnly = input.match(/^\$[\d,]+$/);
  if (dollarAmountOnly) {
    const amount = parseDollarAmount(dollarAmountOnly[0]);
    return {
      percentage: null,
      value: amount !== null ? currencyFormatter.format(amount) : null,
    };
  }

  // Check for percentage only, including decimal percentages
  const percentageOnly = input.match(/^(\d+\.?\d*)%$/);
  if (percentageOnly) {
    return {
      percentage: parseFloat(percentageOnly[1]),
      value: null,
    };
  }

  return defaultResult;
}

/**
 * Extract just the percentage from a coverage value
 * @param value The value to extract the percentage from
 * @returns The extracted percentage or undefined
 */
export function extractPercentageFromValue(value: unknown): number | undefined {
  return parseCoverageValue(value).percentage ?? undefined;
}

/**
 * Extract just the dollar amount from a coverage value
 * @param value The value to extract the dollar amount from
 * @returns The extracted dollar amount or undefined
 */
export function extractDollarAmountFromValue(value: unknown): string | undefined {
  return parseCoverageValue(value).value ?? undefined;
}

/**
 * Check if a value represents a percentage coverage
 * @param value The value to check
 * @returns True if the value is a percentage coverage
 */
export function isPercentageCoverage(value: unknown): boolean {
  return typeof value === 'string' && value.endsWith('%');
}
